import React from 'react';
import { MousePointer2, Circle, Square, Minus, Ruler, Upload, Save, RotateCcw, Hash, Columns, Spline, Box, Download, Pencil, GitBranch, CircleDot, RectangleHorizontal, Hexagon, CircleDashed, Grip, Construction } from 'lucide-react';
import { TOOLS } from '../utils/constants';

const TOOL_CONFIG = [
  {
    id: TOOLS.SELECT,
    icon: MousePointer2,
    label: 'Auswählen',
    key: 'V',
    tooltip: `AUSWÄHLEN (V)
━━━━━━━━━━━━━━━━━━━━
Klick auf Objekt: Auswählen
Shift+Klick: Zur Auswahl hinzufügen
Linksklick+Ziehen (leer): Selektionsrechteck
  • Links→Rechts (blau): Window-Modus
    Wählt nur vollständig eingeschlossene Objekte
  • Rechts→Links (grün): Crossing-Modus
    Wählt alle berührten Objekte

Navigation:
  • Mittlere/Rechte Maustaste: Ansicht verschieben
  • Mausrad: Zoom`
  },
  {
    id: TOOLS.POINT,
    icon: Circle,
    label: 'Punkt setzen',
    key: 'P',
    tooltip: `PUNKT SETZEN (P)
━━━━━━━━━━━━━━━━━━━━
Klick: Neuen Punkt erstellen
Klick auf Wand: Wand teilen
Alt+Klick: Punkt OHNE Wandteilung

Punkte sind Eckpunkte für Wände und Räume.
Die Höhe (Z) wird für die 3D-Wandhöhe verwendet.`
  },
  {
    id: TOOLS.SPACE,
    icon: Square,
    label: 'Raum definieren',
    key: 'R',
    tooltip: `RAUM DEFINIEREN (R)
━━━━━━━━━━━━━━━━━━━━
Klick auf Punkte: Punkte nacheinander auswählen
(mind. 3 Punkte für einen Raum)

Enter: Raum aus ausgewählten Punkten erstellen
Escape: Auswahl abbrechen

Räume definieren Flächen für Mengenberechnung.`
  },
  {
    id: TOOLS.ARC,
    icon: Spline,
    label: 'Bogen',
    key: 'A',
    tooltip: `BOGEN (A)
━━━━━━━━━━━━━━━━━━━━
3-Punkt-Bogen:
1. Klick: Startpunkt
2. Klick: Punkt auf dem Bogen
3. Klick: Endpunkt

Erstellt eine Bogen-Kurve als Geometrie.
Shift+W: Wand aus ausgewähltem Bogen erstellen.`
  },
  {
    id: TOOLS.BOX,
    icon: Box,
    label: 'Quader',
    key: 'B',
    tooltip: `QUADER (B)
━━━━━━━━━━━━━━━━━━━━
Klick: Mittelpunkt setzen
→ Panel für Dimensionen

Erstellt einen 3D-Quader mit:
• Origin (Mittelpunkt)
• Lokalen Achsen (X, Y, Z)
• Breite, Tiefe, Höhe

Quader können in 2D und 3D
angezeigt und bearbeitet werden.`
  },
  { separator: true, label: 'Geometrie' },
  {
    id: TOOLS.LINE,
    icon: Pencil,
    label: 'Linie',
    key: 'L',
    tooltip: `LINIE (L)
━━━━━━━━━━━━━━━━━━━━
1. Klick: Startpunkt
2. Klick: Endpunkt

Erstellt eine gerade Linie zwischen
zwei Punkten. Linien sind die Basis
für Wände und andere Bauteile.`
  },
  {
    id: TOOLS.POLYLINE,
    icon: GitBranch,
    label: 'Polylinie',
    key: 'N',
    tooltip: `POLYLINIE (N)
━━━━━━━━━━━━━━━━━━━━
Klick: Punkte nacheinander setzen
Enter: Polylinie abschließen
Escape: Abbrechen

Erstellt eine verbundene Linie
aus mehreren Segmenten.`
  },
  {
    id: TOOLS.CIRCLE,
    icon: CircleDot,
    label: 'Kreis',
    key: 'K',
    tooltip: `KREIS (K)
━━━━━━━━━━━━━━━━━━━━
1. Klick: Mittelpunkt
2. Klick/Ziehen: Radius definieren

Erstellt einen Kreis mit beliebigem
Radius um den Mittelpunkt.`
  },
  {
    id: TOOLS.RECTANGLE,
    icon: RectangleHorizontal,
    label: 'Rechteck',
    key: 'E',
    tooltip: `RECHTECK (E)
━━━━━━━━━━━━━━━━━━━━
1. Klick: Erste Ecke
2. Klick: Gegenüberliegende Ecke

Erstellt ein achsparalleles Rechteck
zwischen zwei Eckpunkten.`
  },
  {
    id: TOOLS.POLYGON,
    icon: Hexagon,
    label: 'Polygon',
    key: 'Y',
    tooltip: `POLYGON (Y)
━━━━━━━━━━━━━━━━━━━━
Klick: Punkte nacheinander setzen
Enter: Polygon schließen
Escape: Abbrechen

Erstellt ein geschlossenes Polygon
aus beliebig vielen Punkten.`
  },
  {
    id: TOOLS.CYLINDER,
    icon: CircleDashed,
    label: 'Zylinder',
    key: 'J',
    tooltip: `ZYLINDER (J)
━━━━━━━━━━━━━━━━━━━━
1. Klick: Basismittelpunkt
2. Klick/Ziehen: Radius definieren
→ Panel für Höhe

Erstellt einen 3D-Zylinder mit
einstellbarem Radius und Höhe.`
  },
  {
    id: TOOLS.SPHERE,
    icon: Grip,
    label: 'Kugel',
    key: 'U',
    tooltip: `KUGEL (U)
━━━━━━━━━━━━━━━━━━━━
1. Klick: Mittelpunkt
2. Klick/Ziehen: Radius definieren

Erstellt eine 3D-Kugel mit
beliebigem Radius.`
  },
  { separator: true, label: 'Hilfslinien' },
  {
    id: TOOLS.CONSTRUCTION_LINE,
    icon: Construction,
    label: 'Hilfslinie',
    key: 'H',
    tooltip: `HILFSLINIE (H)
━━━━━━━━━━━━━━━━━━━━
1. Klick: Erster Punkt
2. Klick: Zweiter Punkt (Richtung)

Erstellt eine unendliche Hilfslinie
zur Ausrichtung von Objekten.
Hilfslinien werden gestrichelt
dargestellt und nicht gedruckt.`
  },
  {
    id: TOOLS.MEASURE,
    icon: Ruler,
    label: 'Messen',
    key: 'M',
    tooltip: `MESSEN (M)
━━━━━━━━━━━━━━━━━━━━
Klick: Messpunkt setzen
Enter: Messung abschließen
Escape: Messung abbrechen

Funktionen:
• Beliebig viele Punkte setzen
• Strecken werden vermaßt angezeigt
• Ab 3 Punkten: Fläche wird berechnet
• Am Ende: Punkte behalten oder als Raum erstellen`
  },
];

export function ToolbarPanel({
  activeTool,
  selectTool,
  onImportOBJ,
  onSave,
  onReset,
  onExport3dm,
  pendingCount = 0,
  embedded = false,
}) {
  const handleImportClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.obj';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        onImportOBJ?.(file);
      }
    };
    input.click();
  };

  const content = (
    <>
      {/* Tool-Buttons */}
      {TOOL_CONFIG.map((item, index) => {
        // Separator
        if (item.separator) {
          return (
            <div key={`sep-${index}`} className="border-t my-2 pt-2">
              <span className="text-xs text-gray-400 px-2">{item.label}</span>
            </div>
          );
        }

        const { id, icon: Icon, label, key, tooltip } = item;
        return (
          <button
            key={id}
            onClick={() => selectTool(id)}
            className={`p-2 rounded flex items-center gap-2 transition-colors ${
              activeTool === id
                ? 'bg-blue-500 text-white'
                : 'hover:bg-gray-100 text-gray-700'
            }`}
            title={tooltip}
          >
            <Icon size={20} />
            <span className="text-sm whitespace-nowrap">{label}</span>
            <span className="text-xs opacity-50 ml-auto">{key}</span>
          </button>
        );
      })}

      {/* Pending-Anzeige */}
      {pendingCount > 0 && (
        <div className="px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded">
          {pendingCount} Punkt{pendingCount > 1 ? 'e' : ''} ausgewählt
        </div>
      )}

      {/* Trennlinie */}
      <div className="border-t my-2" />

      {/* Aktions-Buttons */}
      <button
        onClick={handleImportClick}
        className="p-2 rounded flex items-center gap-2 hover:bg-gray-100 text-gray-700"
        title={`OBJ IMPORTIEREN
━━━━━━━━━━━━━━━━━━━━
Importiert eine .OBJ-Datei als Grundriss.

Die Linien werden zu Wänden konvertiert,
Schnittpunkte werden automatisch erkannt.`}
      >
        <Upload size={20} />
        <span className="text-sm">OBJ Import</span>
      </button>

      <button
        onClick={onSave}
        className="p-2 rounded flex items-center gap-2 hover:bg-green-100 text-green-700"
        title={`SPEICHERN (Strg+S)
━━━━━━━━━━━━━━━━━━━━
Speichert den aktuellen Grundriss.

Der Editor bleibt geöffnet,
Änderungen werden ins Projekt übernommen.`}
      >
        <Save size={20} />
        <span className="text-sm">Speichern</span>
      </button>

      {onExport3dm && (
        <button
          onClick={onExport3dm}
          className="p-2 rounded flex items-center gap-2 hover:bg-purple-100 text-purple-700"
          title={`3DM EXPORT
━━━━━━━━━━━━━━━━━━━━
Exportiert den Grundriss als .3dm Datei
(Rhino 3D Format).

Die Datei enthält:
• Wände als 3D-Körper
• Räume als Kurven
• Layer-Struktur

Kann in Rhino, Grasshopper oder
anderen CAD-Programmen geöffnet werden.`}
        >
          <Download size={20} />
          <span className="text-sm">3DM Export</span>
        </button>
      )}

      <button
        onClick={onReset}
        className="p-2 rounded flex items-center gap-2 hover:bg-red-100 text-red-700"
        title={`ZURÜCKSETZEN
━━━━━━━━━━━━━━━━━━━━
Setzt den Grundriss auf den letzten
gespeicherten Stand zurück.

ACHTUNG: Alle ungespeicherten
Änderungen gehen verloren!`}
      >
        <RotateCcw size={20} />
        <span className="text-sm">Zurücksetzen</span>
      </button>
    </>
  );

  // Im embedded-Modus nur den Inhalt zurückgeben
  if (embedded) {
    return <div className="p-2 flex flex-col gap-1">{content}</div>;
  }

  // Standalone mit eigenem Container
  return (
    <div
      className="absolute left-4 top-4 bg-white rounded-lg shadow-lg p-2 flex flex-col gap-1 z-10"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {content}
    </div>
  );
}
